import { Table, type InputRef } from 'antd';
import { type ColumnFilterItem, type FilterDropdownProps } from 'antd/es/table/interface';
import { pick } from 'lodash/fp';
import { type Key, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type PromoCodeListingDataFragment } from '../../../api/fragments/PromoCodeListingData';
import { useGetPromoCodeListingQuery } from '../../../api/queries/getPromoCodeListing';
import { CompanyFilterListCollection } from '../../../api/types';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import { useMultipleDealerIds } from '../../../components/contexts/DealerContextManager';
import FilterBox from '../../../components/FilterBox';
import { type PaginatedTableProps } from '../../../components/PaginatedTable';
import PaginatedTableWithContext from '../../../components/PaginatedTableWithContext';
import InternalErrorResult from '../../../components/results/InternalErrorResult';
import SearchBox from '../../../components/SearchBox';
import { checkHasFilter } from '../../../utilities/common';
import { getDateTimeOffsetFormat } from '../../../utilities/date';
import renderBooleanIcon from '../../../utilities/renderBooleanIcon';
import renderFilterIcon from '../../../utilities/renderFilterIcon';
import renderSearchIcon from '../../../utilities/renderSearchIcon';
import useGetCompanyFilter from '../../../utilities/useGetCompanyFilter';
import useSortAndFilterCache, { SortAndFilterCacheKey } from '../../../utilities/useSortAndFilterCache';
import useListReducer from './useListReducer';
import useSortOrder, { getSortField } from './useSortOrder';

type PromoCodeDataSource = PromoCodeListingDataFragment & { key: Key };
type PaginatedPromoCodeTable = PaginatedTableProps<PromoCodeDataSource>;

export const renderInitialValue = (value: PromoCodeListingDataFragment) =>
    value.promoType.__typename === 'DiscountPromoType' ? value.promoType.amount : '-';
const PromoCodeList = () => {
    const [currentCache, setCache] = useSortAndFilterCache(SortAndFilterCacheKey.PromoCode);

    const [state, dispatch] = useListReducer(currentCache);
    const { page, pageSize, sort, filter } = state;
    const navigate = useNavigate();

    const { t } = useTranslation('promoCodeList');
    const inputRef = useRef<InputRef>(null);

    const { dealerIds } = useMultipleDealerIds();
    const company = useCompany(true);
    const { data, loading, error } = useGetPromoCodeListingQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            sort: pick(['field', 'order'], sort),
            filter: { dealerIds, companyIds: company ? [company.id] : undefined, ...filter },
        },
    });

    const dataSource = useMemo(() => (data?.promoCode.items || []).map(item => ({ ...item, key: item.id })), [data]);
    const total = data?.promoCode.count || 0;

    const companyList = useGetCompanyFilter({ collection: CompanyFilterListCollection.PromoCodes, dealerIds });

    const onRow = useCallback<PaginatedPromoCodeTable['onRow']>(
        record => ({
            onClick: () => {
                setCache(state);
                navigate(record.id);
            },
        }),
        [navigate, setCache, state]
    );

    const showSearchDropDown = useCallback(
        (props: FilterDropdownProps, dataIndex: string) =>
            SearchBox({
                filterDropDownProps: props,
                dataIndex,
                onRef: node => {
                    inputRef.current = node;
                },
            }),
        [inputRef]
    );

    const showFilterDropDown = useCallback(
        (props: FilterDropdownProps, filters: ColumnFilterItem[]) =>
            FilterBox({
                filterDropDownProps: props,
                filters,
            }),
        []
    );

    const onChange = useCallback<PaginatedPromoCodeTable['onChange']>(
        (pagination, filters, sorter, extra) => {
            switch (extra.action) {
                case 'sort':
                    return dispatch({
                        type: 'setSort',
                        sort: getSortField(sorter),
                    });

                case 'filter': {
                    let companyFilter: string[] | undefined;

                    if (company) {
                        companyFilter = [company.id];
                    } else {
                        companyFilter = !filters.company ? undefined : (filters.company as string[]);
                    }

                    return dispatch({
                        type: 'setFilter',
                        filter: {
                            promoCode: !filters.promoCode ? null : (filters.promoCode[0] as string),
                            dealerIds,
                            companyIds: companyFilter,
                        },
                    });
                }

                default:
                    return undefined;
            }
        },
        [company, dealerIds, dispatch]
    );

    const { companySort, promoCodeSort, promoTypeSort, activeSort } = useSortOrder(sort);

    const hasFilter = useMemo(() => checkHasFilter(filter), [filter]);

    if (!loading) {
        if (error) {
            return <InternalErrorResult />;
        }
    }

    return (
        <PaginatedTableWithContext
            company={company}
            dataSource={dataSource}
            dispatch={dispatch}
            loading={loading}
            onChange={onChange}
            onRow={onRow}
            rowKey="id"
            state={state}
            tableName={t('promoCodeList:title')}
            total={total}
        >
            {!company && (
                <Table.Column
                    key="company"
                    dataIndex={['module', 'company', 'displayName']}
                    filterDropdown={props => showFilterDropDown(props, companyList)}
                    filterIcon={renderFilterIcon}
                    filteredValue={filter?.companyIds}
                    sortOrder={companySort}
                    title={t('promoCodeList:columns.company')}
                    sorter
                />
            )}
            <Table.Column
                key="promoCode"
                dataIndex="promoCode"
                filterDropdown={props => showSearchDropDown(props, 'promoCode')}
                filterIcon={renderSearchIcon}
                filteredValue={filter?.promoCode ? [filter.promoCode] : undefined}
                sortOrder={promoCodeSort}
                title={t('promoCodeList:columns.promoCode')}
                sorter
            />
            <Table.Column
                key="amount"
                dataIndex={['promoType', 'amount']}
                render={(value, record: PromoCodeListingDataFragment) => renderInitialValue(record)}
                sortOrder={promoTypeSort}
                title={t('promoCodeList:columns.promoTypeAmount')}
                sorter
            />
            <Table.Column
                key="displayName"
                dataIndex={['module', 'displayName']}
                title={t('promoCodeList:columns.moduleDisplayName')}
            />
            <Table.Column
                key="start"
                dataIndex={['period', 'start']}
                render={(value, record: PromoCodeListingDataFragment) =>
                    getDateTimeOffsetFormat(value, t, record.module.company.timeZone)
                }
                title={t('promoCodeList:columns.startDate')}
            />
            <Table.Column
                key="end"
                dataIndex={['period', 'end']}
                render={(value, record: PromoCodeListingDataFragment) =>
                    getDateTimeOffsetFormat(value, t, record.module.company.timeZone)
                }
                title={t('promoCodeList:columns.endDate')}
            />
            <Table.Column
                key="isActive"
                align="center"
                dataIndex="isActive"
                render={value => renderBooleanIcon(value, true)}
                sortOrder={activeSort}
                title={t('promoCodeList:columns.isActive')}
                width={120}
                sorter
            />
        </PaginatedTableWithContext>
    );
};
export default PromoCodeList;
